import { useMemo } from "react";
import { useSelector } from "react-redux";

/**
 * Hook to calculate total cart quantity with optimistic updates
 *
 * This hook combines server cart data with optimistic quantity updates
 * to provide a consistent cart quantity across all components.
 *
 * Used by:
 * - DesktopHeader (cart badge)
 * - MobileHeader (cart badge)
 * - MiniCart (total quantity)
 * - Any component that needs to display cart quantity
 *
 * @returns total quantity of items in cart with optimistic updates applied
 */
export function useOptimisticCartQuantity(): number {
  const cart = useSelector((state: any) => state.cart);
  const { groups, optimisticQuantities } = cart;

  const totalQuantity = useMemo(() => {
    try {
      if (!groups || !Array.isArray(groups)) {
        return 0;
      }

      return groups.reduce((total: number, group: any) => {
        if (!group?.items || !Array.isArray(group.items)) {
          return total;
        }

        return (
          total +
          group.items.reduce((groupTotal: number, product: any) => {
            if (!product || !product.cartCustomId) {
              return groupTotal;
            }

            // Use optimistic quantity if available, otherwise use server quantity
            const quantity =
              optimisticQuantities[product.cartCustomId] !== undefined
                ? optimisticQuantities[product.cartCustomId]
                : product.quantity || 0;

            if (
              typeof quantity === "number" &&
              !isNaN(quantity) &&
              quantity >= 0
            ) {
              return groupTotal + quantity;
            }

            return groupTotal;
          }, 0)
        );
      }, 0);
    } catch (error) {
      console.error("❌ Error calculating optimistic cart quantity:", error);
      return 0;
    }
  }, [groups, optimisticQuantities]);

  return totalQuantity;
}

/**
 * Hook to get optimistic quantity for a specific cart item
 *
 * @param cartCustomId - The cart custom ID of the item
 * @param defaultQuantity - Fallback quantity if no optimistic update exists
 * @returns optimistic quantity or default quantity
 */
export function useOptimisticItemQuantity(
  cartCustomId: string,
  defaultQuantity: number = 0
): number {
  const optimisticQuantities = useSelector(
    (state: any) => state.cart.optimisticQuantities
  );

  return optimisticQuantities[cartCustomId] !== undefined
    ? optimisticQuantities[cartCustomId]
    : defaultQuantity;
}
